import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import SectorService from '../service/sector.services'
import {TCreateSector, TSector, TUpdateSector} from '../types/sector.type'
import {ErrorHelper} from '../types/app.type'
import {toast} from 'sonner'
import {getTranslation} from '../utils/getTranslation'

export const useListSectorsByNetwork = (networkId: string | undefined) => {
  const {data, isLoading} = useQuery<TSector[], ErrorHelper>({
    queryKey: ['sector', 'listByNetwork', networkId ?? ''],
    queryFn: () => SectorService.listSectorsByNetwork(networkId as string),
    enabled: Boolean(networkId),
    // Session-stable reference data: invalidated by create/update/delete mutations.
    staleTime: Infinity,
  })
  return {data, isLoading}
}

const invalidateSectorQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  queryClient.invalidateQueries({queryKey: ['sector']})
  queryClient.invalidateQueries({queryKey: ['projectStatic']})
  // Sectors are geo-references surfaced in the demand form, which reads the
  // network-scoped `withGeoZones` query (staleTime: Infinity, keyed by
  // networkId OR projectId). Invalidate the whole prefix so both cache entries
  // refetch on next mount.
  queryClient.invalidateQueries({queryKey: ['network', 'withGeoZones']})
  // The frise (linear-view viewport, staleTime: Infinity) projects this sector
  // too — invalidate it or the sector only moves after a full page reload.
  queryClient.invalidateQueries({queryKey: ['network', 'viewport']})
}

export const useCreateSectors = () => {
  const queryClient = useQueryClient()
  const {
    mutate: createSectors,
    mutateAsync: createSectorsAsync,
    isPending,
    error,
    isSuccess,
  } = useMutation<unknown, ErrorHelper, TCreateSector[]>({
    mutationFn: items =>
      Promise.all(items.map(item => SectorService.createSector(item))),
    onSuccess: () => {
      invalidateSectorQueries(queryClient)
      toast.success(getTranslation('sector:createSuccess'))
    },
    onError: err => {
      toast.error(getTranslation('error:creationError'), {
        description: err.message,
      })
    },
  })
  return {
    createSectors,
    createSectorsAsync,
    isPending,
    error,
    isSuccess,
  }
}

export const useUpdateSectors = () => {
  const queryClient = useQueryClient()
  const {
    mutate: updateSectors,
    mutateAsync: updateSectorsAsync,
    isPending,
    error,
    isSuccess,
  } = useMutation<unknown, ErrorHelper, TUpdateSector[]>({
    mutationFn: items =>
      Promise.all(items.map(item => SectorService.updateSector(item))),
    onSuccess: () => {
      invalidateSectorQueries(queryClient)
      toast.success(getTranslation('sector:updateSuccess'))
    },
    onError: err => {
      toast.error(getTranslation('error:modificationError'), {
        description: err.message,
      })
    },
  })
  return {
    updateSectors,
    updateSectorsAsync,
    isPending,
    error,
    isSuccess,
  }
}

export const useDeleteSectors = () => {
  const queryClient = useQueryClient()
  const {
    mutate: deleteSectors,
    mutateAsync: deleteSectorsAsync,
    isPending,
    error,
    isSuccess,
  } = useMutation<unknown, ErrorHelper, string[]>({
    mutationFn: ids =>
      Promise.all(ids.map(id => SectorService.deleteSector(id))),
    onSuccess: () => {
      invalidateSectorQueries(queryClient)
      toast.success(getTranslation('sector:deleteSuccess'))
    },
    onError: err => {
      toast.error(getTranslation('error:deleteError'), {
        description: err.message,
      })
    },
  })
  return {
    deleteSectors,
    deleteSectorsAsync,
    isPending,
    error,
    isSuccess,
  }
}
